//src/pages/CustomerReviewsPage.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import { getPublicProductReviews } from "../services/publicReviews.api";

function CustomerReviewsPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const TEXT = {
    EN: {
      title: "Customer Reviews",
      subtitle: "Real reviews from ESIELL customers.",
      loading: "Loading reviews...",
      empty: "No customer reviews yet.",
      verifiedBuyer: "Verified buyer",
      viewProduct: "View product",
      reviews: "reviews",
      review: "review",
    },
    AR: {
      title: "مراجعات العملاء",
      subtitle: "آراء حقيقية من عملاء ESIELL.",
      loading: "جاري تحميل المراجعات...",
      empty: "لا توجد مراجعات بعد.",
      verifiedBuyer: "عميل موثّق",
      viewProduct: "عرض المنتج",
      reviews: "مراجعات",
      review: "مراجعة",
    },
  }[lang];

  useEffect(() => {
    let mounted = true;

    setLoading(true);

    getPublicProductReviews()
      .then((data) => {
        if (!mounted) return;
        setReviews(Array.isArray(data.reviews) ? data.reviews : []);
      })
      .catch((err) => {
        console.error(err);
        if (mounted) setReviews([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const averageRating = useMemo(() => {
    if (!reviews.length) return 0;

    const sum = reviews.reduce((acc, r) => acc + Number(r.rating || 0), 0);
    return sum / reviews.length;
  }, [reviews]);

  const renderStars = (rating) => {
    const safeRating = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));

    return (
      <span className="text-yellow-500 tracking-[1px]">
        {"★".repeat(safeRating)}
        <span className="text-[#D8CDC8]">{"★".repeat(5 - safeRating)}</span>
      </span>
    );
  };

  const formatReviewDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString(lang === "AR" ? "ar" : "en", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const openProduct = (seoSlug) => {
    if (!seoSlug) return;
    navigate(`/product/${seoSlug}`);
  };

  const getReviewMediaItems = (review) => {
  if (
    Array.isArray(review.reviewMediaItems) &&
    review.reviewMediaItems.length > 0
  ) {
    return review.reviewMediaItems.filter(
      (item) => item?.url && item?.type
    );
  }

  if (review.reviewMediaUrl && review.reviewMediaType) {
    return [
      {
        id: `legacy-${review.id}`,
        type: review.reviewMediaType,
        url: review.reviewMediaUrl,
        mimeType: review.reviewMediaMimeType || null,
        filename: review.reviewMediaFilename || null,
      },
    ];
  }

  return [];
};

const renderReviewMediaItem = (media, reviewId, index) => {
  const key = media.id || `${reviewId}-${index}`;

  if (media.type === "image") {
    return (
      <button
        key={key}
        type="button"
        onClick={() => window.open(media.url, "_blank", "noopener,noreferrer")}
        className="block w-full overflow-hidden rounded-xl border border-[#E6D8D2] bg-[#FFF9F6]"
      >
        <img
          src={media.url}
          alt={`Customer review ${index + 1}`}
          loading="lazy"
          className="h-56 sm:h-64 w-full object-contain"
        />
      </button>
    );
  }

  if (media.type === "video") {
    return (
      <div
        key={key}
        className="overflow-hidden rounded-xl border border-[#E6D8D2] bg-black"
      >
        <video
          controls
          playsInline
          preload="metadata"
          className="h-56 sm:h-64 w-full object-contain"
        >
          <source
            src={media.url}
            type={media.mimeType || "video/mp4"}
          />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  if (media.type === "audio") {
    return (
      <div
        key={key}
        className="rounded-xl border border-[#E6D8D2] bg-[#FFF9F6] p-3"
      >
        <p className="mb-2 text-xs text-[#7A7674]">
          {lang === "EN"
            ? `Voice review ${index + 1}`
            : `مراجعة صوتية ${index + 1}`}
        </p>

        <audio controls preload="metadata" className="w-full">
          <source
            src={media.url}
            type={media.mimeType || "audio/ogg"}
          />
          Your browser does not support the audio tag.
        </audio>
      </div>
    );
  }

  return (
    <a
      key={key}
      href={media.url}
      target="_blank"
      rel="noreferrer"
      className="block rounded-xl border border-[#E6D8D2] bg-[#FFF9F6] p-3 text-sm font-semibold text-black underline"
    >
      {media.filename ||
        (lang === "EN" ? "Open review media" : "عرض وسائط المراجعة")}
    </a>
  );
};

const renderReviewMedia = (review) => {
  const mediaItems = getReviewMediaItems(review);

  if (mediaItems.length === 0) return null;

  return (
    <div
      className={`mt-4 grid gap-3 ${
        mediaItems.length === 1
          ? "grid-cols-1"
          : "grid-cols-1 sm:grid-cols-2"
      }`}
    >
      {mediaItems.map((media, index) =>
        renderReviewMediaItem(media, review.id, index)
      )}
    </div>
  );
};

  return (
    <div className="w-full bg-white min-h-screen flex flex-col overflow-x-hidden">
      <TopBanner />
      <MainHeader />
      <CartDrawer />
      <AddedToBagToast />

      <main
        className="w-full bg-white pt-[105px] px-4 pb-16"
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-[#2E3135]">
              {TEXT.title}
            </h1>

            <p className="text-sm text-[#7A7674] mt-2">
              {TEXT.subtitle}
            </p>

            {reviews.length > 0 && (
              <div className="mt-4 inline-flex items-center gap-3 px-5 py-3 rounded-full border border-[#E6D8D2] bg-[#FFF9F6]">
                <span className="text-xl font-bold text-[#2E3135]">
                  {averageRating.toFixed(1)}
                </span>
                <span>{renderStars(averageRating)}</span>
                <span className="text-xs text-[#7A7674]">
                  {reviews.length} {reviews.length === 1 ? TEXT.review : TEXT.reviews}
                </span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12 text-sm text-[#7A7674]">
              {TEXT.loading}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#7A7674]">
              {TEXT.empty}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-white border border-[#E6D8D2] rounded-2xl p-4 shadow-sm"
                >
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => openProduct(review.seoSlug)}
                      className="w-20 h-20 rounded-xl border border-[#E6D8D2] overflow-hidden bg-[#F7F3F1] flex-shrink-0"
                    >
                      <img
                        src={review.productImage}
                        alt={review.productNameEn}
                        className="w-full h-full object-cover"
                      />
                    </button>

                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        onClick={() => openProduct(review.seoSlug)}
                        className={`block font-semibold text-[#2E3135] hover:underline line-clamp-2 ${
                          lang === "AR" ? "text-right" : "text-left"
                        }`}
                      >
                        {lang === "AR"
                          ? review.productNameAr || review.productNameEn
                          : review.productNameEn || review.productNameAr}
                      </button>

                      {review.publicId && (
                        <p className="text-[11px] text-[#9F9087] mt-1">
                          {review.publicId}
                        </p>
                      )}

                      <div className="mt-2 text-sm">
                        {renderStars(review.rating)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {(review.customerName || "C")
                          .trim()
                          .charAt(0)
                          .toUpperCase()}
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#2E3135] truncate">
                          {review.customerName}
                        </p>
                        <p className="text-[11px] text-green-700 font-medium">
                          ✓ {TEXT.verifiedBuyer}
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-[#9F9087] flex-shrink-0">
                      {formatReviewDate(review.createdAt)}
                    </p>
                  </div>

                  {review.reviewText && (
                    <p className="mt-4 text-sm leading-6 text-[#5c5150] whitespace-pre-line">
                      {review.reviewText}
                    </p>
                  )}

                  {renderReviewMedia(review)}

                  <button
                    type="button"
                    onClick={() => openProduct(review.seoSlug)}
                    className="mt-4 w-full py-3 bg-black text-white rounded-lg text-sm font-semibold tracking-wide active:scale-[0.98] transition"
                  >
                    {TEXT.viewProduct}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default CustomerReviewsPage;
